import { Part } from "../../reducers/models/parts";
import { getIdForPart } from "../../utils/parts";
import useFootprintForPart from "../../utils/hooks/footprints/forPart";
import {
  FootprintSummaryContainer,
  NoFootprintData,
  SUMMARY_HEIGHT_PIXELS,
} from "./FootprintSummaryStyles";
import { Pill } from "../../constants/Styles";
import SVGViewer from "../common/SVGViewer";
import { ReactNode, useState } from "react";
import Inspector from "./Inspector";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../utils/group";
import { useAppSelector } from "../../utils/hooks/store";
import { Footprint } from "../../utils/hooks/footprints/models";

export function makeSilkscreenWhite(footprintSvg: string): string {
  return footprintSvg.replaceAll("#000000", "#FFFFFF");
}

const INSPECTOR_EDITOR_ENABLED = false;

function SummaryContainer({
  footprint,
  children,
}: {
  footprint: Footprint | null;
  children: ReactNode;
}) {
  const { groups } = useAppSelector((state) => state.auth);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const [isFootprintInspectorOpen, setIsFootprintInspectorOpen] =
    useState(false);
  const canOpenInspector =
    footprint !== null || (isAdminOrEngineer && INSPECTOR_EDITOR_ENABLED);

  const onClick = () => {
    if (canOpenInspector) {
      setIsFootprintInspectorOpen(true);
    }
  };

  const finishEdit = () => {
    setIsFootprintInspectorOpen(false);
  };

  return (
    <>
      {isFootprintInspectorOpen && (
        <Inspector
          footprint={footprint}
          isOpen={isFootprintInspectorOpen}
          onRequestClose={finishEdit}
        />
      )}
      <FootprintSummaryContainer
        canOpenInspector={canOpenInspector}
        onClick={onClick}
      >
        {children}
      </FootprintSummaryContainer>
    </>
  );
}

// Dimensions of a 1210 part: 3.2mm by 2.5mm
const MIN_PART_HEIGHT_MM = 2.5;

export const MIN_SCALE = SUMMARY_HEIGHT_PIXELS / MIN_PART_HEIGHT_MM;

function NoFootprintDataComponent({
  footprint,
}: {
  footprint: Footprint | null;
}) {
  return (
    <SummaryContainer footprint={footprint}>
      <NoFootprintData>No footprint data</NoFootprintData>
    </SummaryContainer>
  );
}

export function FootprintSummary({ footprint }: { footprint: Footprint }) {
  if (footprint.physical_info === null) {
    return <NoFootprintDataComponent footprint={footprint} />;
  }

  return (
    <SummaryContainer footprint={footprint}>
      <SVGViewer
        svgInput={{ svg: makeSilkscreenWhite(footprint.physical_info.svg) }}
        frameless
        inert
        minScale={MIN_SCALE}
      />
    </SummaryContainer>
  );
}

export default function PartFootprintSummary({ part }: { part: Part }) {
  const { footprint, loading } = useFootprintForPart(getIdForPart(part));

  if (loading) {
    return (
      <SummaryContainer footprint={footprint}>
        <Pill width={"100%"} />
      </SummaryContainer>
    );
  }

  if (footprint === null) {
    return <NoFootprintDataComponent footprint={footprint} />;
  }

  return <FootprintSummary footprint={footprint} />;
}
